import type { FC } from 'react';
import { Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { DividerWithText } from '@components/DividerWithText';
import InputSelect from '@components/InputSelect';
import { SearchableInputSelect } from '@components/SearchableInputSelect';
import { Show } from '@components/Show';
import { Badge } from '@components/ui/badge';
import { useSubmissions } from '@queries/fetchSubmissions';
import { isEmptyValue } from '@root/helpers';

import { useDuplicateEndorsementContractList } from '../hooks/useDuplicateEndorsementContractList';
import type { StepProps } from '../types';
import { getBlankEndorsementTemplateValues } from '../utils';

export const Step1: FC<
  StepProps & {
    endorsementSummaryTemplates: { value: number; name: string }[];
    isFetchingEndorsementSummaryTemplates: boolean;
    isEdit?: boolean;
  }
> = ({
  control,
  formValues,
  isReadOnly = false,
  isEdit = false,
  setValue,
  endorsementSummaryTemplates,
  isFetchingEndorsementSummaryTemplates,
}) => {
  const urlSubmissionId = useParams().id;
  const currentContractId = formValues.parentSubmissionId ?? urlSubmissionId;

  const { data: submissions = [], isLoading: isLoadingSubmissions } = useSubmissions();
  const duplicateContractOptions = useDuplicateEndorsementContractList({ contracts: submissions, currentContractId });

  const hasTemplateSelected = !isEmptyValue(formValues.endorsement_summary_template_id);
  const hasDuplicateContractSelected = !isEmptyValue(formValues.duplicateSourceContractId);

  return (
    <div className="flex flex-col">
      <Controller
        control={control}
        name="endorsement_summary_template_id"
        render={({ field, fieldState: { error } }) => (
          <InputSelect
            {...field}
            isClearable
            isFetchingOptions={isFetchingEndorsementSummaryTemplates}
            isReadOnly={isReadOnly}
            isDisabled={hasDuplicateContractSelected}
            isSearchable={false}
            labelText="Select a blank endorsement template"
            options={endorsementSummaryTemplates}
            errors={error?.message}
            dataTestId="select:endorsement-summary-template-id"
            shouldRenderInsidePortal
            value={formValues.endorsement_summary_template_id}
            onChange={(value) => {
              field.onChange(value);
              setValue?.('duplicateSourceContractId', undefined);
              setValue?.('duplicateSourceEndorsementId', undefined);
            }}
          />
        )}
      />

      <Show when={!isEdit}>
        <DividerWithText id="endorsement-source">Or</DividerWithText>

        <Controller
          control={control}
          name="duplicateSourceContractId"
          render={({ field: { ref: _ref, onChange, ...field }, fieldState: { error } }) => (
            <SearchableInputSelect
              {...field}
              isDisabled={isReadOnly || hasTemplateSelected}
              isLoading={isLoadingSubmissions}
              noOptionsLeftLabel="No contracts available to duplicate from"
              labelText="Duplicate an existing endorsement"
              placeholder="Search contracts"
              options={duplicateContractOptions}
              value={formValues.duplicateSourceContractId}
              errors={error?.message}
              dataTestId="select:duplicate-source-contract-id"
              onChange={(value, name) => {
                onChange(value, name);
                setValue?.('duplicateSourceEndorsementId', undefined);
                if (!value) {
                  const blankValues = getBlankEndorsementTemplateValues();
                  (Object.keys(blankValues) as (keyof typeof blankValues)[]).forEach((blankField) =>
                    setValue?.(blankField, blankValues[blankField]),
                  );
                }
              }}
              renderOption={(option) => {
                const isCurrentContract = duplicateContractOptions.find(
                  (o) => o.value === option.value,
                )?.isCurrentContract;
                return (
                  <div className="flex items-center justify-between gap-3">
                    <span className="truncate">{option.name}</span>
                    {isCurrentContract && (
                      <Badge variant="outline" className="bg-primary-100 text-primary-700 shrink-0 border-transparent">
                        This contract
                      </Badge>
                    )}
                  </div>
                );
              }}
            />
          )}
        />
      </Show>
    </div>
  );
};
